import React from 'react';
import { motion } from 'framer-motion';
import {
    Cloud, Server, Database, Shield, Zap,
    ArrowRight, ChevronLeft, Cpu, Globe,
    Layers, HardDrive, Settings, Activity, RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO_Component from '../../components/SEO';

const CloudSolutions = () => {
    const cloudFeatures = [
        { title: "Infrastructure", desc: "Highly available and scalable server setups.", icon: Server },
        { title: "Migration", desc: "Seamless transition of legacy data to the cloud.", icon: RefreshCw },
        { title: "DevOps", desc: "CI/CD pipelines for rapid, reliable deployments.", icon: Settings },
        { title: "Data Storage", desc: "Encrypted and redundant cloud database systems.", icon: Database },
        { title: "Monitoring", desc: "Real-time health and performance tracking.", icon: Activity },
        { title: "Edge Networks", desc: "Global content delivery for zero-latency.", icon: Globe }
    ];

    return (
        <div className="bg-[#02000c] min-h-screen text-white pt-20 overflow-hidden">
            <SEO_Component
                title="Cloud Solutions | Scalable & Secure Infrastructure - MehareTech"
                description="Engineer a global-scale cloud architecture with MehareTech. We provide secure server management, seamless cloud migration, and high-availability database solutions."
                keywords="cloud infrastructure, cloud migration services, devops consulting, AWS management, Azure solutions, server scalability, cloud database security"
            />

            {/* Back to Services Link */}
            <div className="container mx-auto px-6 mb-4">
                <Link to="/services" className="inline-flex items-center gap-2 text-cyan-500 font-bold uppercase tracking-widest text-[10px] hover:text-white transition-colors group">
                    <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Services
                </Link>
            </div>

            {/* Hero Section */}
            <section className="relative py-12 px-6">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-cyan-600/10 blur-[120px] -z-10"></div>

                <div className="container mx-auto text-center max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-cyan-500 font-black uppercase tracking-[0.3em] text-[10px] mb-2 block">
                            Enterprise Cloud
                        </span>
                        <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tighter">
                            SCALING WITHOUT <span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 bg-clip-text text-transparent">LIMITS</span>
                        </h1>
                        <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto font-medium">
                            We engineer global-scale cloud architectures that power modern digital enterprises.
                        </p>
                        <div className="flex justify-center">
                            <button className="px-8 py-3 bg-cyan-600 text-white font-bold rounded-xl hover:bg-cyan-700 transition-all flex items-center gap-2 text-xs">
                                Configure My Cloud <ArrowRight size={18} />
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Grid */}
            <section className="py-12 px-6 bg-slate-900/40 border-y border-white/5">
                <div className="container mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        {cloudFeatures.map((item, i) => (
                            <motion.div
                                key={i}
                                className="p-6 bg-slate-900 border border-white/5 rounded-2xl hover:border-cyan-500/30 transition-all group"
                            >
                                <item.icon size={26} className="text-cyan-500 mb-4 group-hover:rotate-12 transition-transform" />
                                <h3 className="text-lg font-bold mb-1">{item.title}</h3>
                                <p className="text-slate-400 text-[11px] leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Info Section */}
            <section className="py-12 px-6">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <div className="md:w-1/2">
                            <h2 className="text-3xl font-black mb-6 tracking-tight">
                                99.9% <span className="text-cyan-500">Uptime</span>
                            </h2>
                            <div className="space-y-4">
                                <div className="p-4 bg-white/5 border border-white/5 rounded-xl flex items-center gap-4">
                                    <Cpu className="text-cyan-500" size={24} />
                                    <div>
                                        <h4 className="font-bold text-sm">Automated Scaling</h4>
                                        <p className="text-[10px] text-slate-400">Resources that grow with your traffic demand.</p>
                                    </div>
                                </div>
                                <div className="p-4 bg-white/5 border border-white/5 rounded-xl flex items-center gap-4">
                                    <Shield className="text-cyan-500" size={24} />
                                    <div>
                                        <h4 className="font-bold text-sm">Military-Grade Security</h4>
                                        <p className="text-[10px] text-slate-400">End-to-end encryption and identity management.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="md:w-1/2">
                            <img
                                src="https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg"
                                alt="Cloud Server"
                                className="rounded-3xl shadow-xl w-full h-48 object-cover border border-white/10"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CloudSolutions;
